import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, X, Share } from "lucide-react";

/**
 * usePWAManifest — swap the `<link rel="manifest">` and theme-color meta
 * tags on the fly so the current route (Storefront / Admin / POS / KDS)
 * can be installed as its own separate PWA. Also captures the browser's
 * `beforeinstallprompt` so we can trigger install from an in-app button
 * on Android/desktop Chrome.
 */
export function usePWAManifest({ manifest, themeColor, appleTitle }) {
  const [installEvent, setInstallEvent] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(`pwa_dismiss_${manifest}`) === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    // Swap the manifest link
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    const prev = link.getAttribute("href");
    link.setAttribute("href", manifest);

    // Swap the theme-color meta tag so the OS statusbar matches this surface
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    const prevTheme = meta.getAttribute("content");
    meta.setAttribute("content", themeColor);

    // apple-mobile-web-app-title so iOS home screen shows the surface name
    let appleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleMeta) {
      appleMeta = document.createElement("meta");
      appleMeta.name = "apple-mobile-web-app-title";
      document.head.appendChild(appleMeta);
    }
    const prevAppleTitle = appleMeta.getAttribute("content");
    appleMeta.setAttribute("content", appleTitle);

    return () => {
      // Restore previous values when unmounting so a nav from /pos back to /
      // doesn't leave the POS manifest active.
      if (prev) link.setAttribute("href", prev);
      if (prevTheme) meta.setAttribute("content", prevTheme);
      if (prevAppleTitle) appleMeta.setAttribute("content", prevAppleTitle);
    };
  }, [manifest, themeColor, appleTitle]);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try { localStorage.setItem(`pwa_dismiss_${manifest}`, "1"); } catch {}
  }, [manifest]);

  const install = useCallback(async () => {
    if (!installEvent) return false;
    try {
      installEvent.prompt();
      const choice = await installEvent.userChoice;
      setInstallEvent(null);
      if (choice.outcome === "accepted") {
        toast.success(`${appleTitle} installed to your home screen!`);
        dismiss();
      }
      return choice.outcome === "accepted";
    } catch {
      return false;
    }
  }, [installEvent, appleTitle, dismiss]);

  const isIOS = /iP(ad|hone|od)/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
     window.matchMedia("(display-mode: fullscreen)").matches ||
     window.navigator.standalone === true);

  return { installEvent, install, dismiss, dismissed, isIOS, isStandalone };
}


/**
 * InstallAppBanner — small non-intrusive banner that shows how to add this
 * surface to the device's home screen. Chrome/Android show a native prompt
 * (button click); iOS needs manual instructions via the Share menu.
 * Hides itself once installed OR dismissed.
 */
export function InstallAppBanner({ manifest, themeColor, appleTitle, label }) {
  const pwa = usePWAManifest({ manifest, themeColor, appleTitle });
  const shouldShow =
    !pwa.dismissed &&
    !pwa.isStandalone &&
    (pwa.installEvent || pwa.isIOS);
  if (!shouldShow) return null;
  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 inset-x-4 z-40 sm:left-auto sm:right-4 sm:max-w-sm bg-stone-900 text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3"
    >
      <Download className="w-6 h-6 text-brand-yellow shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-black text-sm">Install {label}</div>
        {pwa.isIOS ? (
          <div className="text-[11px] text-white/70 mt-0.5 flex items-center gap-1 flex-wrap">
            Tap <Share className="w-3 h-3 inline" /> Share → <b>Add to Home Screen</b>
          </div>
        ) : (
          <div className="text-[11px] text-white/70 mt-0.5">
            One-tap install so it opens like a native app.
          </div>
        )}
      </div>
      {!pwa.isIOS && (
        <button
          onClick={pwa.install}
          data-testid="pwa-install-btn"
          className="bg-brand-yellow text-stone-900 px-3 py-2 rounded-full text-xs uppercase tracking-widest font-black shrink-0"
        >
          Install
        </button>
      )}
      <button
        onClick={pwa.dismiss}
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss"
        className="text-white/40 hover:text-white shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
